<template>
  <div class="order-detail-content">
    <div class="content-block">
      <p class="content-block__label">{{ $t('booking_detail_orders_number') }}</p>

      <div class="content-block__context">
        <div class="d-flex justify-space-between align-center">
          <div class="block-text">{{ order.id || '' }}</div>

          <v-tooltip v-model="showCopySuccess" color="black50" location="top">
            <template #activator="{ props }">
              <v-btn :ref="props.ref" min-width="32" height="32" variant="text" :disabled="showCopySuccess" @click="copyOrderID">
                <funnow-icon class="mt-1" max-width="20" :image-path="showCopySuccess ? 'check-icon_orange.svg' : 'copy.svg'" />
              </v-btn>
            </template>

            <span> {{ $t('copy_success') }} </span>
          </v-tooltip>
        </div>
      </div>
    </div>

    <div v-if="itemAmount" class="content-block">
      <p class="content-block__label">{{ itemAmountLabel }}</p>
      <div class="content-block__context">
        <div class="block-text">{{ itemAmount }}</div>
      </div>
    </div>

    <div class="content-block pb-0">
      <p class="content-block__label">{{ $t('booking_detail_booking_content') }}</p>

      <div class="content-block__context">
        <!-- 升級 -->
        <div v-for="product in order.upgrades" :key="product.id" class="block-text pt-2 pb-4">
          {{ `${product.qty} x ${product.upgrade_name}` }}
        </div>
        <!-- 主商品 -->
        <div v-if="showProductsQty" :key="order.product.id" class="block-text pt-2 pb-4">
          {{ `${order.product.qty} x ${order.product.name}` }}
        </div>
        <!-- 子選項 -->
        <div v-for="(item, productIndex) in productSubitemList" :key="productIndex" class="px-6 py-3">
          <div>{{ item.titleLabel }}</div>
          <span v-for="subitemLabel in item.subitemLabels" :key="subitemLabel" class="wd-text-gray80 pr-1">
            {{ subitemLabel }}
          </span>
        </div>
        <!-- 加購 -->
        <div v-for="product in order.additions" :key="product.id" class="block-text py-4">
          {{ `${product.qty} x ${product.name}` }}
        </div>
      </div>
    </div>

    <div class="content-block">
      <p class="content-block__label">{{ $t('booking_detail_remark') }}</p>

      <div class="content-block__context">
        <div class="block-text">
          <div v-for="(remark, idx) in order.remarks" :key="idx" class="info-content">
            <p class="hint-text mb-1">{{ `${remark.title}: ${remark.content}` }}</p>
          </div>

          <div v-if="!!order.member_note" class="info-content">
            <p class="mb-0" v-html="nl2br(order.member_note)" />
          </div>
        </div>
      </div>
    </div>

    <!-- <div v-if="isPaidOnsite" class="content-block"> -->
    <template v-if="isPaidOnsite">
      <div class="content-block">
        <div class="content-block__label">{{ paidOnsiteUiData.createdLabel }}</div>
        <div class="wd-w-full wd-flex wd-justify-between">
          <div v-for="data in paidOnsiteUiData.createdAtTuple" :key="data">{{ data }}</div>
        </div>
      </div>
      <div class="content-block wd-flex wd-justify-between">
        <div>{{ $t('pay_at_store') }}</div>
        <div v-show="order.txn_amount" class="wd-text-orange50">{{ txnText }}</div>
      </div>
      <!-- <div v-if="isCancel" class="content-block"> -->
      <div v-if="order.cancel_date" class="content-block">
        <div class="content-block__label">{{ paidOnsiteUiData.cancelledLabel }}</div>
        <div class="wd-w-full wd-flex wd-justify-between">
          <div v-for="data in paidOnsiteUiData.cancelledTuple" :key="data">{{ data }}</div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import utils from '@/utils'

import FunnowIcon from '@/components/icons/funnow-icon'
import { useAutoResetClipBoard } from '@/composition/useUtil'
import { useOrderFlags, useOrderDetailItemData } from '@/composition/useOrder'
import { timeUtil } from '@/utils/time'
import { usePlatformStore } from '~/store-pinia/platform'

export default defineComponent({
  name: 'OrderDetailSheet',
  components: {
    FunnowIcon,
  },
  props: {
    order: { type: Object, default: () => ({}) },
  },
  setup(props) {
    const nuxtApp = useNuxtApp()
    const { isFunBookV2 } = storeToRefs(usePlatformStore())
    const order = computed(() => props.order)
    const orderID = computed(() => order.value.id)
    const { copy: copyOrderID, copiedAutoReset: showCopySuccess } = useAutoResetClipBoard(orderID)
    const orderFlags = useOrderFlags(order)
    const orderDetailItemData = useOrderDetailItemData(order)

    /** different UI info between FunNow and FunBook2 */
    const paidOnsiteUiData = computed(() => ({
      /** @type {string} */
      createdLabel: isFunBookV2.value ? nuxtApp.$i18n.t('create_time') : nuxtApp.$i18n.t('booking_detail_booking_info'),
      /** @type {[string, string]} [ title, created time ] */
      createdAtTuple: [isFunBookV2.value ? '' : nuxtApp.$i18n.t('booking_channel_funbook'), timeUtil.formatDateTime(order.value.created_at)].filter(
        Boolean
      ),
      /** @type {string} */
      cancelledLabel: isFunBookV2.value ? nuxtApp.$i18n.t('cancelled_time') : nuxtApp.$i18n.t('cancel_info'),
      /** @type {[string, string]} [ title, cancelled time ] */
      cancelledTuple: [isFunBookV2.value ? '' : nuxtApp.$i18n.t('cancelled_time'), timeUtil.formatDateTime(order.value.cancel_date, true)].filter(
        Boolean
      ),
    }))

    function nl2br(text) {
      return utils.nl2br(text) || ''
    }

    return {
      copyOrderID,
      showCopySuccess,
      ...orderFlags,
      ...orderDetailItemData,
      paidOnsiteUiData,
      nl2br,
      formatDateTime: timeUtil.formatDateTime,
    }
  },
})
</script>
<style lang="scss" scoped></style>
